/**
 * Cross-Tab Refresh Lock Coordination
 * Ensures only one tab refreshes tokens at a time using Web Locks API.
 * Falls back to BroadcastChannel if locks unavailable.
 *
 * Primary: navigator.locks.request('auth_refresh', ...)
 * Fallback 1: BroadcastChannel('auth_refresh')
 * Fallback 2: In-memory flag (single-tab only)
 */

import logger from './logger';

const supportsWebLocks = () =>
  typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function';

const supportsBroadcastChannel = () => typeof BroadcastChannel !== 'undefined';

let fallbackRefreshInProgress = false;
let broadcastChannel = null;

const initBroadcastChannel = () => {
  if (broadcastChannel || !supportsBroadcastChannel()) return;
  try {
    broadcastChannel = new BroadcastChannel('auth_refresh');
    broadcastChannel.onmessage = (event) => {
      if (event.data.type === 'refresh_in_progress') {
        fallbackRefreshInProgress = true;
      } else if (event.data.type === 'refresh_complete') {
        fallbackRefreshInProgress = false;
      }
    };
  } catch (err) {
    logger.warn('[TokenRefreshLock] Failed to init BroadcastChannel:', err.message);
    broadcastChannel = null;
  }
};

/**
 * Acquire cross-tab refresh lock and execute callback.
 * Only one tab's callback runs at a time (browser-wide mutex).
 *
 * @param {function} callback - async function to execute under lock
 * @returns {Promise} - result of callback or null if lock couldn't be acquired
 */
export const acquireRefreshLock = async (callback) => {
  if (!callback) return null;

  // PRIMARY: Web Locks API
  if (supportsWebLocks()) {
    try {
      let result = null;
      await navigator.locks.request('auth_refresh', async () => {
        result = await callback();
      });
      return result;
    } catch (err) {
      logger.error('[TokenRefreshLock] Web Locks error:', err.message);
    }
  }

  // FALLBACK 1: BroadcastChannel
  if (supportsBroadcastChannel()) {
    initBroadcastChannel();

    const maxWaitMs = 10000;
    const startTime = Date.now();
    while (fallbackRefreshInProgress && Date.now() - startTime < maxWaitMs) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (fallbackRefreshInProgress) {
      logger.warn('[TokenRefreshLock] Timeout waiting for other tab refresh');
      return null;
    }

    fallbackRefreshInProgress = true;
    if (broadcastChannel) {
      try {
        broadcastChannel.postMessage({ type: 'refresh_in_progress' });
      } catch (err) {
        logger.warn('[TokenRefreshLock] Failed to broadcast:', err.message);
      }
    }

    try {
      const result = await callback();
      fallbackRefreshInProgress = false;
      if (broadcastChannel) {
        try {
          broadcastChannel.postMessage({ type: 'refresh_complete' });
        } catch (err) {
          logger.warn('[TokenRefreshLock] Failed to broadcast complete:', err.message);
        }
      }
      return result;
    } catch (err) {
      fallbackRefreshInProgress = false;
      if (broadcastChannel) {
        try {
          broadcastChannel.postMessage({ type: 'refresh_complete' });
        } catch (e) {
          logger.warn('[TokenRefreshLock] Failed to broadcast on error:', e.message);
        }
      }
      throw err;
    }
  }

  // FALLBACK 2: In-memory flag
  if (fallbackRefreshInProgress) {
    logger.warn('[TokenRefreshLock] Refresh already in progress');
    return null;
  }

  fallbackRefreshInProgress = true;
  try {
    return await callback();
  } finally {
    fallbackRefreshInProgress = false;
  }
};

export const cleanupRefreshLock = () => {
  if (broadcastChannel) {
    try {
      broadcastChannel.close();
    } catch (err) {
      logger.warn('[TokenRefreshLock] Error closing BroadcastChannel:', err.message);
    }
    broadcastChannel = null;
  }
};
